import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import config from './config';
import ErrorBoundary from './components/ErrorBoundary';
import ExamView from './components/ExamView';
import PrerequisitesCheck from './components/PrerequisitesCheck';
import AdminDashboard from './components/AdminDashboard';
import ProgramList from './components/ProgramList';
import { updateEnrollmentProgress } from './utils/enrollmentUtils';


// Fixed React Hook dependency warnings
// Certificate prerequisites MVP implementation

const { API_URL, COGNITO_DOMAIN, CLIENT_ID, REDIRECT_URI, DEFAULT_ASSESSMENT } = config;

function App() {
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('login');
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState('');
  const [selectedProgramName, setSelectedProgramName] = useState(''); // Track which program the assessment belongs to

  const [questions, setQuestions] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [examTimer, setExamTimer] = useState(null);
  const [, setTimeRemaining] = useState(0);
  const [examTimeLimit, setExamTimeLimit] = useState(60);
  const [examStartTime, setExamStartTime] = useState(null);


  // Enrollment state management
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Load user enrollments function
  const loadUserEnrollments = useCallback(async () => {
    if (!user?.email) {
      setUserEnrollments([]);
      return;
    }

    setEnrollmentsLoading(true);
    try {
      // Demo mode - provide demo enrollments
      if (user?.token === 'demo-token-for-local-testing') {
        const demoEnrollments = [
          {
            user_email: user.email,
            program_name: 'Cloud Operator Demo',
            status: 'active',
            enrollment_date: new Date().toISOString(),
            last_activity: new Date().toISOString()
          },
          {
            user_email: user.email,
            program_name: 'DevOps Fundamentals Demo',
            status: 'inactive',
            enrollment_date: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
            unenrollment_date: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
            last_activity: new Date(Date.now() - 86400000).toISOString()
          }
          // Note: AWS Basics Demo is left available for enrollment testing
        ];
        setUserEnrollments(demoEnrollments);
        setEnrollmentsLoading(false);
        return;
      }

      // Real API call for production
      const response = await fetch(`${API_URL}/admin/enrollments?user_email=${encodeURIComponent(user.email)}`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserEnrollments(data || []);
      } else {
        setUserEnrollments([]);
      }
    } catch (error) {
      console.error('Error loading user enrollments:', error);
      setUserEnrollments([]);
    } finally {
      setEnrollmentsLoading(false);
    }
  }, [user?.email, user?.token]);

  // Function to update enrollment status (for unenroll/reenroll)
  const updateEnrollmentStatus = useCallback((userEmail, programName, newStatus) => {
    setUserEnrollments(prev => prev.map(enrollment =>
      enrollment.user_email === userEmail && enrollment.program_name === programName
        ? {
          ...enrollment,
          status: newStatus,
          unenrollment_date: newStatus === 'inactive' ? new Date().toISOString() : null,
          last_activity: newStatus === 'active' ? new Date().toISOString() : enrollment.last_activity
        }
        : enrollment
    ));
  }, []);

  // Function to add new enrollment (for demo mode)
  const addNewEnrollment = useCallback((userEmail, programName) => {
    const newEnrollment = {
      user_email: userEmail,
      program_name: programName,
      status: 'active',
      enrollment_date: new Date().toISOString(),
      last_activity: new Date().toISOString()
    };

    setUserEnrollments(prev => {
      // Check if enrollment already exists
      const existingIndex = prev.findIndex(e =>
        e.user_email === userEmail && e.program_name === programName
      );

      if (existingIndex >= 0) {
        // Update existing enrollment to active
        return prev.map((enrollment, index) =>
          index === existingIndex
            ? { ...enrollment, status: 'active', last_activity: new Date().toISOString(), unenrollment_date: null }
            : enrollment
        );
      } else {
        // Add new enrollment
        return [...prev, newEnrollment];
      }
    });
  }, []);

  const loadAssessments = useCallback(async () => {
    setAssessmentsLoading(true);
    try {
      // Demo mode - provide demo assessments
      if (user?.token === 'demo-token-for-local-testing') {
        console.log('Demo mode: Using demo assessments');
        const demoAssessments = [
          { name: 'Cloud Operator Demo', totalQuestions: 3, timeLimit: 30 },
          { name: 'DevOps Fundamentals Demo', totalQuestions: 3, timeLimit: 30 },
          { name: 'AWS Basics Demo', totalQuestions: 3, timeLimit: 30 }
        ];
        setAssessments(demoAssessments);
        setAssessmentsLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/questions?type=assessments`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const assessmentConfigs = await response.json();

      if (assessmentConfigs && Array.isArray(assessmentConfigs) && assessmentConfigs.length > 0) {
        setAssessments(assessmentConfigs);
      } else {
        const questionsResponse = await fetch(`${API_URL}/admin/questions`);
        if (!questionsResponse.ok) {
          throw new Error(`HTTP ${questionsResponse.status}`);
        }
        const questions = await questionsResponse.json();
        const uniqueAssessments = [...new Set(questions.map(q => q.assessment_name).filter(name => name))];
        const assessmentList = uniqueAssessments.map(name => ({ name }));
        setAssessments(assessmentList.length > 0 ? assessmentList : [{ name: DEFAULT_ASSESSMENT }]);
      }
    } catch (error) {
      console.error('Error loading assessments:', error);
      setAssessments([{ name: DEFAULT_ASSESSMENT }]);
    } finally {
      setAssessmentsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('id_token');
      if (!token) return;

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('id_token');
          return;
        }

        const isAdmin = payload['cognito:groups'] && payload['cognito:groups'].includes('Admin');
        setUser({ email: payload.email || 'Unknown User', isAdmin, token });
        // Only set initial view if not already set
        setCurrentView(prev => prev === 'login' ? 'programs' : prev);
        await loadAssessments();
      } catch (e) {
        console.error('Token validation failed:', e);
      }
    };

    checkAuthentication();

    // Cleanup timer on unmount
    return () => {
      if (examTimer) {
        clearInterval(examTimer);
      }
    };
  }, [loadAssessments, examTimer]);

  // Load enrollments when user changes
  useEffect(() => {
    if (user) {
      loadUserEnrollments();
    }
  }, [user, loadUserEnrollments]);

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const login = () => {
    const authUrl = `https://${COGNITO_DOMAIN}/oauth2/authorize?` +
      `response_type=token&` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `scope=email+openid+profile&` +
      `identity_provider=AzureAD`;
    window.location.href = authUrl;
  };



  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    setUser(null);
    setCurrentView('login');
  };

  const startExam = async (assessmentName, programName = null) => {
    const assessment = assessmentName || selectedAssessment;
    console.log('startExam called, assessment:', assessment, 'program:', programName);

    if (!assessment) {
      console.log('No assessment selected');
      setMessage({ text: 'Please select an assessment first.', type: 'error' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // Update selectedAssessment and selectedProgramName if passed as parameters
    if (assessmentName && assessmentName !== selectedAssessment) {
      setSelectedAssessment(assessmentName);
    }
    if (programName) {
      setSelectedProgramName(programName);
    }

    // Show prerequisites check first
    setCurrentView('prerequisites');
  };

  // Called when prerequisites are met
  const onPrerequisitesMet = async () => {
    console.log('Prerequisites met, loading exam');

    // TEMPORARY: Disable strict frontend authentication check for debugging
    // Backend will still enforce security
    if (!user) {
      console.log('No user object found - but allowing for debugging');
      console.log('User state:', user);
      // Don't block - let backend handle security
    } else {
      console.log('User found:', { email: user.email, hasToken: !!user.token });
    }

    // Continue with assessment regardless of frontend auth check
    console.log('Proceeding with assessment start (backend will enforce security)');

    // Backend enforces one-attempt policy - no frontend check needed
    console.log('Starting exam for:', selectedAssessment);
    setLoading(true);
    setMessage(null);

    try {
      // Demo mode - use local questions if user is demo user
      if (user?.token === 'demo-token-for-local-testing') {
        console.log('Demo mode: Using local demo questions');
        const demoQuestions = [
          {
            id: 'demo-q1',
            question: 'What is the primary benefit of using Infrastructure as Code (IaC)?',
            choices: [
              'Faster deployment times',
              'Version control and reproducibility',
              'Lower costs',
              'Better security'
            ],
            correct_answer: 'Version control and reproducibility',
            assessment_name: selectedAssessment,
            category: 'DevOps'
          },
          {
            id: 'demo-q2',
            question: 'Which AWS service is primarily used for container orchestration?',
            choices: [
              'EC2',
              'Lambda',
              'EKS',
              'S3'
            ],
            correct_answer: 'EKS',
            assessment_name: selectedAssessment,
            category: 'AWS'
          },
          {
            id: 'demo-q3',
            question: 'What does CI/CD stand for?',
            choices: [
              'Continuous Integration/Continuous Deployment',
              'Code Integration/Code Deployment',
              'Container Integration/Container Deployment',
              'Cloud Integration/Cloud Deployment'
            ],
            correct_answer: 'Continuous Integration/Continuous Deployment',
            assessment_name: selectedAssessment,
            category: 'DevOps'
          }
        ];

        setExamTimeLimit(30); // 30 minutes for demo
        setQuestions(demoQuestions);
        setExamStartTime(Date.now());
        setCurrentView('exam');
        setLoading(false);
        return;
      }

      const url = `${API_URL}/questions?assessment=${encodeURIComponent(selectedAssessment)}`;
      console.log('Fetching questions from:', url);

      const response = await fetch(url, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });
      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('Error response:', { status: response.status, errorData });

        // Handle authentication error
        if (response.status === 401) {
          console.log('Authentication required');
          setMessage({
            text: '🔐 Authentication Required: Please log in to access assessments.',
            type: 'error'
          });
          setTimeout(() => setMessage(null), 5000);
          setLoading(false);
          return;
        }

        // Handle attempt limit error - check for specific error types
        if (response.status === 403 && errorData.error) {
          if (errorData.error === 'Assessment Already Completed' || errorData.contact_admin) {
            console.log('Assessment already completed - blocking user');
            let message = '🚫 Assessment Already Completed\n\n';
            message += errorData.message || 'You have already completed this assessment. Only one attempt is allowed per assessment.';

            if (errorData.score !== null && errorData.score !== undefined) {
              message += `\n\nYour previous score: ${errorData.score}% (${errorData.passed ? 'PASSED' : 'FAILED'})`;
            }

            if (errorData.completion_date) {
              const date = new Date(parseInt(errorData.completion_date) * 1000).toLocaleDateString();
              message += `\nCompleted on: ${date}`;
            }

            message += '\n\n📧 Please contact an administrator if you need to retake this assessment.';

            setMessage({
              text: message,
              type: 'error'
            });
            setTimeout(() => setMessage(null), 15000); // Longer timeout for detailed message
            setLoading(false);
            return;
          }

          // Handle other 403 errors (enrollment, etc.)
          const errorMessage = errorData.error.toLowerCase();
          if (errorMessage.includes('already completed') ||
            errorMessage.includes('only one attempt') ||
            errorMessage.includes('you have already completed') ||
            errorMessage.includes('one attempt is allowed')) {
            console.log('Attempt limit detected (legacy format)');
            setMessage({
              text: '⚠️ You have already completed this assessment. Only one attempt is allowed. Contact an administrator if you need to retake it.',
              type: 'error'
            });
            setTimeout(() => setMessage(null), 10000);
            setLoading(false);
            return;
          }

          // Handle enrollment errors
          if (errorMessage.includes('enrollment') || errorMessage.includes('access denied')) {
            console.log('Enrollment error detected');
            setMessage({
              text: '🚫 Access Denied: You must be enrolled in a program that includes this assessment. Please contact an administrator.',
              type: 'error'
            });
            setTimeout(() => setMessage(null), 8000);
            setLoading(false);
            return;
          }
        }

        // Handle any other 403 errors
        if (response.status === 403) {
          console.log('Generic 403 error');
          setMessage({
            text: '🚫 Access Denied: You do not have permission to access this assessment. Please contact an administrator.',
            type: 'error'
          });
          setTimeout(() => setMessage(null), 8000);
          setLoading(false);
          return;
        }

        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Questions response data:', data);

      if (data.error) {
        console.log('API returned error:', data.error);
        setMessage({ text: 'Error: ' + data.error, type: 'error' });
        setTimeout(() => setMessage(null), 5000);
        setLoading(false);
        return;
      }

      // Handle both old and new API response formats
      const questionsData = data.questions || data;
      const timeLimit = data.timeLimit || 60;
      console.log('Questions data:', questionsData, 'Time limit:', timeLimit);

      // Store time limit for the enhanced exam view
      setExamTimeLimit(timeLimit);

      const questionsArray = Array.isArray(questionsData) ? questionsData : [];
      console.log('Questions array length:', questionsArray.length);

      if (questionsArray.length === 0) {
        console.log('No questions found');
        setMessage({ text: 'No questions found for this assessment. Please contact the administrator.', type: 'error' });
        setTimeout(() => setMessage(null), 5000);
        setLoading(false);
        return;
      }

      const invalidQuestions = questionsArray.filter(q => !q.question || !q.choices || !Array.isArray(q.choices));
      if (invalidQuestions.length > 0) {
        console.log('Invalid questions found:', invalidQuestions);
        setMessage({ text: 'Some questions have invalid format. Please contact the administrator.', type: 'error' });
        setTimeout(() => setMessage(null), 5000);
        setLoading(false);
        return;
      }

      console.log('Setting questions and starting exam');
      setQuestions(questionsArray);
      console.log('About to set currentView to exam');
      setExamStartTime(Date.now());
      setCurrentView('exam');
      console.log('CurrentView set to exam');
    } catch (error) {
      console.error('Error loading questions:', error);
      setMessage({ text: 'Error loading questions: ' + error.message, type: 'error' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setLoading(false);
    }
  };



  // Legacy submit function - now handled by EnhancedExamView
  // const submitExam = async () => { ... }



  const LoginView = () => (
    <div className="login-container">
      <div className="login-card">
        <h2>Welcome</h2>
        <p>Please authenticate with your Azure AD account to access the assessment platform.</p>
        <button className="btn btn-primary" onClick={login}>Login with Azure AD</button>
      </div>
    </div>
  );

  const ProgramsView = () => {
    return (
      <ProgramList
        user={user}
        onMessage={setMessage}
        userEnrollments={userEnrollments}
        enrollmentsLoading={enrollmentsLoading}
        onEnrollmentUpdate={loadUserEnrollments}
        onNewEnrollment={addNewEnrollment}
        onStartAssessment={(assessmentName, programName) => {
          startExam(assessmentName, programName);
        }}
      />
    );
  };

  const AssessmentSelection = () => {
    if (assessmentsLoading) {
      return (
        <div className="card">
          <h2>Select Assessment</h2>
          <div className="loading">Loading assessments...</div>
        </div>
      );
    }

    const hasAssessments = assessments && assessments.length > 0;

    return (
      <div className="card">
        <h2>Select Assessment</h2>

        {/* One Attempt Warning */}
        <div style={{
          background: '#f8d7da',
          border: '2px solid #dc3545',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '25px'
        }}>
          <h4 style={{ color: '#721c24', margin: '0 0 15px 0', fontSize: '1.1em' }}>
            📋 Assessment Policy
          </h4>
          <div style={{ color: '#721c24', fontSize: '0.95em', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
              You can take each assessment ONLY ONCE. There are no retakes without administrator approval.
            </p>
            <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
              <li>Once you start, the attempt is recorded</li>
              <li>Closing the browser or losing connection will NOT reset your attempt</li>
              <li>Make sure you have a stable internet connection</li>
              <li>Ensure you have enough time to complete the full assessment</li>
            </ul>
            <p style={{ margin: '10px 0 0 0', fontWeight: 'bold' }}>
              Contact an administrator if you need to reset an attempt.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label>Choose an assessment to begin:</label>
          <select
            value={selectedAssessment}
            onChange={(e) => setSelectedAssessment(e.target.value)}
            disabled={loading || !hasAssessments}
          >
            <option value="">Select an assessment...</option>
            {hasAssessments && assessments.map((assessment, index) => {
              return (
                <option
                  key={`${assessment.name}-${index}`}
                  value={assessment.name}
                >
                  {assessment.name} {assessment.totalQuestions ? `(${assessment.totalQuestions} questions)` : ''}
                </option>
              );
            })}
          </select>
          {!hasAssessments && (
            <p style={{ color: '#666', fontSize: '0.9em', marginTop: '5px' }}>No assessments available.</p>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={(e) => {
            e.preventDefault();
            console.log('=== START ASSESSMENT CLICKED ===');
            console.log('Button disabled?', e.target.disabled);
            console.log('Loading:', loading);
            console.log('Selected Assessment:', selectedAssessment);
            console.log('Has Assessments:', hasAssessments);
            console.log('API_URL:', API_URL);
            if (!e.target.disabled) {
              startExam();
            }
          }}
          disabled={loading || !selectedAssessment || !hasAssessments}
        >
          {loading ? 'Loading...' : 'Start Assessment'}
        </button>
      </div>
    );
  };

  const EnhancedExamView = () => {
    console.log('ExamView rendering, questions length:', questions?.length);

    if (!questions || questions.length === 0) {
      console.log('No questions available in ExamView');
      return (
        <div className="card">
          <h2>No Questions Available</h2>
          <p>No questions were found for this assessment. Please contact the administrator.</p>
          <button className="btn btn-secondary" onClick={() => {
            if (examTimer) {
              clearInterval(examTimer);
              setExamTimer(null);
              setTimeRemaining(0);

            }
            setCurrentView('assessments');
          }}>← Back to Assessments</button>
        </div>
      );
    }

    const handleExamSubmit = (submissionData) => {
      console.log('=== EXAM SUBMISSION STARTED ===');
      console.log('Submission data:', submissionData);

      // Convert submission data to the format expected by the API
      const examAnswers = submissionData.map(item => ({
        id: item.id,
        selected_answer: item.selected_answer
      }));

      console.log('Exam answers:', examAnswers);

      // Add user email and assessment name to the submission
      const submissionPayload = {
        answers: examAnswers,
        user_email: user?.email || 'anonymous',
        assessment_name: selectedAssessment,
        time_taken_seconds: examStartTime ? Math.round((Date.now() - examStartTime) / 1000) : null
      };

      console.log('Submission payload:', submissionPayload);
      console.log('API URL:', API_URL);

      fetch(`${API_URL}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify(submissionPayload)
      })
        .then(response => {
          console.log('Response status:', response.status);
          console.log('Response headers:', response.headers);
          if (response.status === 403) {
            // Handle one-attempt policy blocking
            return response.json().then(errorData => {
              console.log('403 error data:', errorData);
              if (errorData.error && (errorData.error.includes('already completed') || errorData.error.includes('only one attempt'))) {
                setMessage({
                  text: '⚠️ Assessment Already Completed: You have already taken this assessment. Only one attempt is allowed per assessment. Contact an administrator if you need to reset your attempt.',
                  type: 'error'
                });
                setTimeout(() => setMessage(null), 10000);
                setCurrentView('assessments'); // Go back to assessment selection
                return;
              }
              throw new Error(`Access denied: ${errorData.error || 'Unknown error'}`);
            });
          }
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then(examResult => {
          console.log('Exam result:', examResult);

          // The 403 (already-completed) branch above handles its own UI and
          // returns undefined; nothing more to do here in that case.
          if (!examResult) {
            console.log('=== EXAM SUBMISSION HANDLED (no result payload) ===');
            return;
          }

          // Backend tracks attempts - no frontend storage needed
          setResult(examResult);
          setCurrentView('result');
          console.log('=== EXAM SUBMISSION COMPLETED ===');

          // Update enrollment progress to mark assessment as completed ONLY if passed
          if (user?.email && selectedAssessment && selectedProgramName && examResult.passed) {
            console.log('User passed - updating enrollment progress for program:', selectedProgramName, 'assessment:', selectedAssessment);
            updateEnrollmentProgress(user.email, selectedProgramName, selectedAssessment, user.token)
              .then(result => {
                if (result.success) {
                  console.log('Enrollment progress updated successfully');
                  // Reload enrollments to refresh the UI
                  if (loadUserEnrollments) {
                    loadUserEnrollments();
                  }
                } else {
                  console.error('Failed to update enrollment progress:', result.message);
                }
              })
              .catch(error => {
                console.error('Error updating enrollment progress:', error);
              });
          } else {
            if (!examResult.passed) {
              console.log('User did not pass - enrollment progress not updated');
            } else if (!selectedProgramName) {
              console.warn('No program name available for assessment:', selectedAssessment);
            }
          }
        })
        .catch(error => {
          console.error('Exam submission error:', error);
          console.log('=== EXAM SUBMISSION FAILED ===');
          setMessage({ text: 'Error submitting exam: ' + error.message, type: 'error' });
          setTimeout(() => setMessage(null), 5000);
        });
    };

    return (
      <ExamView
        questions={questions}
        selectedAssessment={selectedAssessment}
        timeLimit={examTimeLimit}
        onSubmit={handleExamSubmit}
        shuffleQuestions={true}
        shuffleChoices={true}
      />
    );
  };

  const ResultView = () => {
    const score = Number(result?.score) || 0;
    const correct = Number(result?.correct) || 0;
    const total = Number(result?.total) || 0;
    const passed = Boolean(result?.passed);

    return (
      <div className={`result ${passed ? 'pass' : 'fail'}`}>
        <h2>Assessment {passed ? 'PASSED' : 'FAILED'}</h2>
        <p>Score: {score.toFixed(1)}%</p>
        <p>Correct Answers: {correct} out of {total}</p>
        <p>{passed
          ? 'Congratulations! You have successfully demonstrated your technical knowledge.'
          : 'You need 70% or higher to pass. To retake this assessment, please contact the Cloud Team at EnproCloudServices@enpro.com.'
        }</p>
        <button className="btn btn-primary" onClick={() => {
          if (examTimer) {
            clearInterval(examTimer);
            setExamTimer(null);
          }
          setCurrentView('programs');
          setResult(null);
          setTimeRemaining(0);
          setSelectedAssessment(''); // Reset selection
          setQuestions([]); // Clear questions
        }}>Return to Programs</button>
      </div>
    );
  };







  return (
    <ErrorBoundary>
      <div className="App">
        <header className="header">
          <div className="container header-content">
            <div className="header-left">
              {(!user || !user.isAdmin || currentView !== 'administration') && (
                <>
                  <h1>EnPro Cloud Assessment</h1>
                  <p>Technical Knowledge Certification Platform</p>
                </>
              )}
              {user && user.isAdmin && currentView === 'administration' && (
                <>
                  <h1>Administration</h1>
                  <p>Manage assessments, questions, and enrollments</p>
                </>
              )}
            </div>
            {user && (
              <div className="header-right">
                <span className="user-email">{user.email}</span>
                {user.isAdmin && currentView !== 'administration' && (
                  <button className="btn btn-admin" onClick={() => {
                    if (examTimer) {
                      clearInterval(examTimer);
                      setExamTimer(null);
                      setTimeRemaining(0);
                    }
                    setCurrentView('administration');
                  }}>Administration</button>
                )}
                <button className="btn btn-logout" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <main className="main">
          <div className="container">
            {message && (
              <div className={`message ${message.type}`} style={{
                position: 'relative',
                whiteSpace: 'pre-line',
                fontSize: message.type === 'error' && message.text.includes('Assessment Already Completed') ? '1.1rem' : '1rem',
                padding: message.type === 'error' && message.text.includes('Assessment Already Completed') ? '1.5rem' : '1rem',
                marginBottom: '2rem',
                boxShadow: message.type === 'error' ? '0 4px 12px rgba(220, 53, 69, 0.2)' : 'none'
              }}>
                {message.text}
                {message.type === 'error' && message.text.includes('Assessment Already Completed') && (
                  <button
                    onClick={() => setMessage(null)}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#721c24',
                      opacity: 0.7
                    }}
                    title="Close message"
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            {loading && (
              <div className="loading">
                <h2>Loading...</h2>
                <p>Please wait while we prepare your assessment.</p>
              </div>
            )}

            {!loading && (
              <>
                {(() => {
                  console.log('Rendering view:', currentView);
                  switch (currentView) {
                    case 'login': return <LoginView />;
                    case 'programs': return <ProgramsView />;
                    case 'assessments': return <AssessmentSelection />;
                    case 'prerequisites': return (
                      <PrerequisitesCheck
                        userEmail={user?.email}
                        programName={selectedProgramName}
                        onPrerequisitesMet={onPrerequisitesMet}
                        onBack={() => {
                          setCurrentView('assessments');
                        }}
                      />
                    );
                    case 'exam': return <EnhancedExamView />;
                    case 'result': return <ResultView />;
                    case 'administration': return user?.isAdmin ? <AdminDashboard
                      user={user}
                      onBack={() => {
                        if (examTimer) {
                          clearInterval(examTimer);
                          setExamTimer(null);
                          setTimeRemaining(0);
                        }
                        setCurrentView('programs');
                      }}
                      onMessage={setMessage}
                      onEnrollmentUpdate={updateEnrollmentStatus}
                      refreshEnrollments={loadUserEnrollments}
                    /> : null;
                    default: return <ProgramsView />;
                  }
                })()}
              </>
            )}
          </div>
        </main>

        <footer className="footer">
          <div className="container footer-content">
            <div className="footer-main">
              <p className="footer-copyright">&copy; 2026 Enpro. All rights reserved.</p>
              <p className="footer-legal">
                This platform processes personal data (email addresses and assessment results) in accordance with GDPR.
                Your data is used solely for authentication, assessment tracking, and certification purposes.
              </p>
            </div>
            <div className="footer-links">
              <a href="#privacy" className="footer-link" onClick={(e) => {
                e.preventDefault();
                alert('Privacy Policy\n\nData Controller: Enpro\n\nData Collected:\n- Email address (via Azure AD authentication)\n- Assessment results and scores\n- Enrollment information\n- Certificate uploads\n\nPurpose: User authentication, assessment management, and certification tracking.\n\nData Retention: Assessment data is retained for certification records. You may request data deletion by contacting your Cloud Manager.\n\nYour Rights: Access, rectification, erasure, and data portability. Contact your Cloud Manager to exercise these rights.\n\nFor more information, contact your Cloud Manager.');
              }}>Privacy Policy</a>
              <span className="footer-separator">•</span>
              <a href="#data-protection" className="footer-link" onClick={(e) => {
                e.preventDefault();
                alert('Data Protection\n\nYour personal data is protected and processed in compliance with GDPR regulations.\n\nData Security:\n- Encrypted transmission (HTTPS)\n- Secure authentication via Azure AD\n- Access controls and audit logging\n- Data stored in AWS with enterprise-grade security\n\nContact your Cloud Manager for data protection inquiries.');
              }}>Data Protection</a>
              <span className="footer-separator">•</span>
              <a href="#contact" className="footer-link" onClick={(e) => {
                e.preventDefault();
                alert('Contact\n\nFor questions about your data, assessment results, or to exercise your GDPR rights, please contact your Cloud Manager.');
              }}>Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
}

export default App;
